import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { fieldWorkApi } from '../../api/field-work-api';
import {
  MAX_NOTE_LENGTH,
  type FieldWorkActivity,
  type FieldWorkRequest,
} from '../../domain/fieldWorkRequest';

function who(email: string | undefined): string {
  return email || 'Unassigned';
}

function describe(entry: FieldWorkActivity): string {
  switch (entry.kind) {
    case 'HANDOFF':
      return `Handed off from ${who(entry.fromAssignee)} to ${who(entry.toAssignee)}`;
    case 'ASSIGNED':
      return `Assigned to ${who(entry.toAssignee)}`;
    default:
      return 'Note';
  }
}

/** Notes and handoffs on one request, newest first, with a box to add a note. */
export function ActivityLog({
  request,
  onChanged,
}: {
  request: FieldWorkRequest;
  onChanged: (updated: FieldWorkRequest) => void;
}) {
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () => fieldWorkApi.addNote(request.id, note),
    onSuccess: (updated) => {
      setNote('');
      setError(null);
      onChanged(updated);
    },
    onError: (failure: unknown) =>
      setError(failure instanceof Error ? failure.message : 'The note could not be saved.'),
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!note.trim()) {
      setError('Write the note first.');
      return;
    }
    add.mutate();
  }

  const entries = [...request.activity].reverse();
  return (
    <section className="drawer-section" aria-labelledby="activity-heading">
      <h2 id="activity-heading">
        Notes and handoffs
        {entries.length > 0 ? <span className="tab-count">{entries.length}</span> : null}
      </h2>
      <form className="activity-form" onSubmit={handleSubmit} aria-label="New note">
        <label>
          Add a note
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={2}
            maxLength={MAX_NOTE_LENGTH}
            placeholder="Progress, findings, where parts are…"
          />
        </label>
        {error ? (
          <p role="alert" className="form-error">
            {error}
          </p>
        ) : null}
        <div className="drawer-actions">
          <button type="submit" disabled={add.isPending}>
            {add.isPending ? 'Saving…' : 'Add note'}
          </button>
        </div>
      </form>
      {entries.length === 0 ? (
        <p className="field-hint">No notes yet.</p>
      ) : (
        <ol className="activity-log">
          {entries.map((entry) => (
            <li key={entry.id} className={`activity-entry activity-${entry.kind.toLowerCase()}`}>
              <div className="activity-meta">
                <strong>{describe(entry)}</strong>
                <span>
                  {entry.author ?? '—'}
                  {entry.createTime ? ` · ${new Date(entry.createTime).toLocaleString()}` : ''}
                </span>
              </div>
              {entry.note ? <p className="activity-note">{entry.note}</p> : null}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
